/* Support-only progressive enhancement. Native details and contact submission remain the fallback. */
(() => {
  if (window.smSupportEnhancementsInstalled) return;
  window.smSupportEnhancementsInstalled = true;
  const instances = new WeakMap();
  function photos(form, signal) {
    const area = form?.querySelector('[data-sm-photos]');
    if (!area) return () => {};
    const input = area.querySelector('[data-sm-file]');
    const choose = area.querySelector('[data-sm-choose]');
    const list = area.querySelector('[data-sm-photo-list]');
    const status = area.querySelector('[data-sm-photo-status]');
    const error = area.querySelector('[data-sm-photo-error]');
    const links = area.querySelector('[data-sm-photo-links]');
    const submit = form.querySelector('[type="submit"]');
    const items = [];
    const endpoint = `${window.Shopify?.routes?.root || '/'}apps/bb-rewards/support`;
    let sessionPromise;
    const session = () => sessionPromise ||= fetch(`${endpoint}/session`, {signal,headers:{Accept:'application/json'}})
      .then(async response => { const data = await response.json(); if (!response.ok || !data.session) throw new Error(data.message || 'Photos are temporarily unavailable. Please try again.'); return data.session; })
      .catch(reason => { sessionPromise = null; throw reason; });
    const update = () => {
      const pending = items.filter(item => item.state === 'uploading').length;
      const failed = items.filter(item => item.state === 'error').length;
      const ready = items.filter(item => item.url);
      if (!pending && !failed && /^(Your photos are still uploading|Retry or remove the photo that)/.test(error.textContent)) error.textContent = '';
      status.textContent = pending ? `Uploading ${pending === 1 ? 'photo' : 'photos'}…` : failed ? 'Retry or remove the photo before sending.' : ready.length ? `${ready.length} ${ready.length === 1 ? 'photo' : 'photos'} ready to send.` : '';
      links.value = ready.map((item,index) => `Photo ${index+1}: ${item.url}`).join('\n');
      links.disabled = !ready.length;
      submit.setAttribute('aria-busy', String(!!pending));
      // The button remains focusable. A blocked attempt explains how to continue.
      submit.dataset.smPhotosPending = String(!!pending || !!failed);
      choose.disabled = items.length >= 3;
    };
    const revokeRemote = (url, token) => {
      const image = url?.split('/').pop();
      if (image && token) fetch(`${endpoint}/images?session=${encodeURIComponent(token)}&image=${encodeURIComponent(image)}`, {method:'DELETE',signal}).catch(() => {});
    };
    const upload = async item => {
      item.state = 'uploading'; item.note.textContent = 'Uploading…'; item.retry.hidden = true;
      item.controller = new AbortController();
      const cancel = () => item.controller.abort();
      signal.addEventListener('abort', cancel, {once:true});
      update();
      try {
        item.session = await session();
        if (!items.includes(item) || signal.aborted) return;
        const response = await fetch(`${endpoint}/images?session=${encodeURIComponent(item.session)}`, {
          method:'POST',body:item.file,signal:item.controller.signal,headers:{'Content-Type':'application/octet-stream',Accept:'application/json'}
        });
        const data = await response.json();
        if (!response.ok || !/^https:\/\/rewards-api-production-2c1d\.up\.railway\.app\/support\/image\/[a-f0-9]{64}$/.test(data.url || '')) {
          if (response.status === 401) sessionPromise = null;
          throw new Error(data.message || 'Photo upload failed. Please try again.');
        }
        if (!items.includes(item)) { revokeRemote(data.url,item.session); return; }
        item.url = data.url; item.state = 'ready'; item.note.textContent = 'Ready';
      } catch (reason) {
        if (!items.includes(item) || signal.aborted) return;
        item.state = 'error'; item.note.textContent = reason.name === 'AbortError' ? 'Upload stopped. Try again.' : reason.message;
        item.retry.hidden = false;
      } finally { signal.removeEventListener('abort',cancel); if (!signal.aborted) update(); }
    };
    const add = file => {
      const item = {file,state:'uploading',preview:URL.createObjectURL(file)};
      const row = document.createElement('li'); row.className = 'sm-support-photo';
      const preview = document.createElement('img'); preview.src = item.preview; preview.alt = '';
      const copy = document.createElement('div'); copy.className = 'sm-support-photo__copy';
      const name = document.createElement('span'); name.textContent = file.name;
      item.note = document.createElement('span'); item.note.className = 'sm-support-photo__note';
      item.retry = document.createElement('button'); item.retry.type = 'button'; item.retry.textContent = 'Retry'; item.retry.hidden = true;
      item.retry.addEventListener('click', () => upload(item), {signal});
      copy.append(name,item.note,item.retry);
      const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'sm-support-photo__remove'; remove.textContent = '×'; remove.setAttribute('aria-label',`Remove ${file.name}`);
      remove.addEventListener('click', () => {
        item.controller?.abort(); revokeRemote(item.url,item.session);
        items.splice(items.indexOf(item),1); URL.revokeObjectURL(item.preview); row.remove(); error.textContent = ''; update(); choose.focus();
      }, {signal});
      row.append(preview,copy,remove); list.append(row); items.push(item); upload(item);
    };
    area.hidden = false; links.disabled = true;
    choose.addEventListener('click', () => input.click(), {signal});
    input.addEventListener('change', () => {
      const selected = [...input.files]; input.value = ''; error.textContent = '';
      const remaining = 3 - items.length;
      if (selected.length > remaining) { error.textContent = `You can add ${remaining ? `${remaining} more ${remaining === 1 ? 'photo' : 'photos'}` : 'up to 3 photos'}. Remove a photo to choose another.`; return; }
      const invalid = selected.find(file => !['image/jpeg','image/png','image/webp'].includes(file.type) || !file.size || file.size > 5*1024*1024);
      if (invalid) { error.textContent = `${invalid.name}: choose a JPG, PNG or WebP under 5 MB.`; return; }
      selected.forEach(add);
    }, {signal});
    const guard = event => {
      if (!items.some(item => item.state !== 'ready')) return;
      event.preventDefault(); event.stopImmediatePropagation();
      error.textContent = items.some(item => item.state === 'error') ? 'Retry or remove the photo that could not upload, then send your message.' : 'Your photos are still uploading. Please wait a moment, then send your message.';
      error.focus();
    };
    form.addEventListener('click', event => { if (event.target.closest('[type="submit"]')) guard(event); }, {capture:true,signal});
    form.addEventListener('submit', guard, {capture:true,signal});
    return () => items.forEach(item => { item.controller?.abort(); URL.revokeObjectURL(item.preview); });
  }
  function init(root) {
    root.querySelectorAll?.('.bb-support').forEach(page => {
      if (instances.has(page)) return;
      const controller = new AbortController();
      const signal = controller.signal;
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const cleanup = [];
      cleanup.push(photos(page.querySelector('.bb-support-form'), signal));
      page.querySelectorAll('.bb-support__faq details').forEach(details => {
        const summary = details.querySelector('summary');
        const body = details.querySelector('.sm-support-reveal');
        if (!summary || !body) return;
        let expanded = details.open, position = 0, velocity = 0, frame = 0, lastTime = 0;
        let target = 0;
        const settle = () => {
          cancelAnimationFrame(frame); frame = 0; velocity = 0;
          details.open = expanded;
          body.style.height = '';
          body.style.overflow = '';
          body.style.opacity = '';
        };
        const tick = time => {
          const dt = Math.min((time - lastTime) / 1000, .032);
          lastTime = time;
          // Analytic critically damped spring: carry current position and velocity on reversal.
          const omega = innerWidth < 750 ? 30 : 24, displacement = position - target;
          const blend = velocity + omega * displacement;
          const decay = Math.exp(-omega * dt);
          position = target + (displacement + blend * dt) * decay;
          velocity = (velocity - omega * blend * dt) * decay;
          body.style.height = `${Math.max(0, position)}px`;
          body.style.opacity = String(Math.min(1, Math.max(0, position / Math.max(body.firstElementChild.getBoundingClientRect().height, 1))));
          if (Math.abs(position - target) < .5 && Math.abs(velocity) < 5) settle();
          else frame = requestAnimationFrame(tick);
        };
        const setOpen = next => {
          if (expanded === next && !frame) return;
          expanded = next;
          summary.setAttribute('aria-expanded', String(next));
          details.dataset.smExpanded = String(next);
          body.inert = !next;
          if (!next && body.contains(document.activeElement)) summary.focus({preventScroll:true});
          if (reduced.matches) { settle(); return; }
          if (!frame) { position = details.open ? body.getBoundingClientRect().height : 0; velocity = 0; }
          details.open = true; // Keep the closing content rendered, but immediately inert to keyboard/AT.
          body.style.overflow = 'hidden';
          body.style.height = `${position}px`;
          target = next ? body.firstElementChild.getBoundingClientRect().height : 0;
          if (!frame) { lastTime = performance.now(); frame = requestAnimationFrame(tick); }
        };
        summary.setAttribute('aria-expanded', String(expanded));
        details.dataset.smExpanded = String(expanded);
        body.inert = !expanded;
        details.smSupportDisclosure = setOpen;
        summary.addEventListener('click', event => { event.preventDefault(); setOpen(!expanded); }, {signal});
        details.addEventListener('toggle', () => {
          if (!frame && details.open !== expanded) setOpen(details.open);
        }, {signal});
        reduced.addEventListener('change', () => { if (reduced.matches) settle(); }, {signal});
        // Re-measure a moving answer if the viewport or its content changes.
        const resize = new ResizeObserver(() => { if (expanded && frame) target = body.firstElementChild.getBoundingClientRect().height; });
        resize.observe(body.firstElementChild);
        cleanup.push(() => {
          resize.disconnect(); settle(); body.inert = false;
          summary.removeAttribute('aria-expanded'); delete details.dataset.smExpanded;
          delete details.smSupportDisclosure;
        });
      });
      instances.set(page, () => { controller.abort(); cleanup.forEach(fn => fn()); instances.delete(page); });
    });
  }
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll?.('.bb-support').forEach(page => instances.get(page)?.()));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), {once:true});
  else init(document);
})();
